import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import VacancyFillFactory from '../VacancyFillFactory';
import useAppDispatch from 'hooks/useAppDispatch';
import { popError, popSuccess } from 'store/slices/popNotifications.slice';
import { useParams } from 'react-router-dom';
import { useFillVacancyMutation } from 'api/schoolGroups.api';
import ValueContext from 'providers/ValueContext/Value.context';
import paramsActions from 'store/actions/params.actions';
import { FillVacancyMethod } from 'searchality-data';

type VacancyFillCandidateModalProps = {
  className?: string;
  vacancyId: string;
  closeModal: () => void;
};

const VacancyFillCandidateModal: React.ForwardRefRenderFunction<
  ModalRef,
  VacancyFillCandidateModalProps
> = (props, ref) => {
  const { className, closeModal } = props;
  const [fillCandidates, { isLoading }] = useFillVacancyMutation();
  const dispatch = useAppDispatch();
  const { vacancyId, schoolGroupId } = useParams();

  const { t } = useTranslation();

  const baseClass = 'vacancy-fill-candidate-modal';

  const classes = classNames(baseClass, className);

  const { selectedIds, clearIds } = useContext(ValueContext);
  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const onFillCandidates = async () => {
    fillCandidates({
      vacancyId,
      schoolGroupId,
      applicantIds: selectedIds,
      fillMethod: FillVacancyMethod.FROM_CANDIDATE_APPLICATIONS,
    })
      .unwrap()
      .then(() => {
        closeModal();
        dispatch(popSuccess(t('vacancySuccesfllyFilled')));
      })
      .catch((e) => {
        dispatch(popError(e));
      });
  };

  const initializeParams = useCallback(
    () =>
      dispatch(
        paramsActions.initializeParams('schoolGroupVacancyCandidates', {
          $limit: 6,
        }),
      ),
    [dispatch],
  );

  useEffect(() => {
    if (modal?.openState === false) {
      clearIds();
    }
  }, [modal?.openState, clearIds]);

  useEffect(() => {
    initializeParams();
    return () => {
      initializeParams();
    };
  }, [initializeParams]);

  return (
    <Modal
      className={classes}
      ref={modalRef}
      title={t('vacancyFillCandidateModalModal.title')}
      footer={
        <>
          <Button variant="outline" onClick={props.closeModal}>
            {t('Cancel')}
          </Button>
          <Button
            disabled={!selectedIds[0] || isLoading}
            onClick={onFillCandidates}
          >
            {t('vacancyFillCandidateModalModal.confirmButton')}
          </Button>
        </>
      }
    >
      {modal?.openState && <VacancyFillFactory />}
    </Modal>
  );
};

export default forwardRef(VacancyFillCandidateModal);
